<template>
  <Loading v-if="componentLoading" :style="style" />
  <Chart v-else-if="visible" class="compare-line-chart" ref="chartComponent" :deep-update="lineAnimate" :option="chartConfig" :style="style"></Chart>
</template>

<script lang="ts" setup>
import Chart from "@p-v/packages/Charts/BaseChart/index.vue"
import Loading from "@p-v/packages/Loading.vue"
import { ref } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import config from "./config"
import useToggle from "@p-v/hooks/useToggle"
import type { DataItem, CompareLineChartProps } from "./index.d"
import type { EChartsOption, LineSeriesOption, LinesSeriesOption, YAXisComponentOption } from "echarts"

const chartComponent = ref<InstanceType<typeof Chart>>()
const componentLoading = ref<boolean>(true)

const props = withDefaults(defineProps<CompareLineChartProps>(), {
  // dataList: () => [],
  config: () => ({}),
  style: () => ({}),
  unitShow: () => [false, false],
  lineAnimate: () => false,
  lineAnimateDuration: () => 2000,
  show: () => true,
})

const { visible, show, hide, changeVisible } = useToggle(props.show)

const chartConfig = ref(cloneDeep(config))

// watch(
//   () => props,
//   propsData => {
//     // console.log('watch props', propsData);
//     if (propsData.dataList.length > 0) {
//       const result = componentPropEngine(propsData)
//       // console.log('result', result.series);
//       chartConfig.value = result
//     } else {
chartConfig.value = cloneDeep(config)
//     }
//   },
//   {
//     immediate: true,
//     deep: true,
//   },
// )

const fillSeries = (data: LineSeriesOption, index: number, seriesTpl: LinesSeriesOption) => {
  if (seriesTpl instanceof Array) {
    if (seriesTpl[index]) {
      return defaultsDeep(data, seriesTpl[index])
    } else {
      return data
    }
  } else if (seriesTpl) {
    return defaultsDeep(data, seriesTpl)
  } else {
    return data
  }
}

interface ColorStop {
  offset: number
  color: string
}

/**
 * 数据处理
 * @param config
 * @param dataList
 */
function componentPropEngine(propsData: CompareLineChartProps): EChartsOption {
  const { dataList, unitShow, config: _config } = propsData
  let seriesTpl: LinesSeriesOption = {}
  if (_config && _config.series) {
    seriesTpl = _config.series as LinesSeriesOption
    delete _config.series
  } else if (props.config && props.config.series) {
    seriesTpl = props.config.series as LinesSeriesOption
    // delete props.config.series
  }
  const newConfig = cloneDeep(config)
  const configFromProp = cloneDeep(_config)
  const yAxisConfig = newConfig.yAxis
  const configResult = defaultsDeep(configFromProp, props.config, newConfig)

  if (dataList) {
    const mainData = dataList[0]
    const compareData = dataList[1]

    if (mainData && compareData) {
      // 配置主轴和对比轴
      configResult.xAxis[0].data = mainData.axis
      configResult.xAxis[1].data = compareData.axis
      configResult.xAxis[0].show = true
      configResult.xAxis[1].show = compareData?.showXAxis
      configResult.xAxis[0].axisPointer.label.formatter = (params: { value: string | number; seriesData: { data: string | number }[] }) => {
        return `${mainData.name} ${params.value}${params.seriesData.length ? `：${params.seriesData[0].data}` : ""}`
      }
      configResult.xAxis[1].axisPointer.label.formatter = (params: { value: string | number; seriesData: { data: string | number }[] }) => {
        return `${compareData.name} ${params.value}${params.seriesData.length ? `：${params.seriesData[0].data}` : ""}`
      }

      // 配置legend
      configResult.legend.data = [mainData.name, compareData.name]

      // 配置数据
      configResult.series[0] = fillSeries(
        {
          name: mainData.name,
          type: "line",
          smooth: true,
          emphasis: {
            disabled: true,
          },
          data: mainData.data,
        },
        0,
        seriesTpl,
      )

      configResult.series[1] = fillSeries(
        {
          name: compareData.name,
          type: "line",
          smooth: true,
          data: compareData.data,
          emphasis: {
            disabled: true,
          },
          xAxisIndex: 1,
        },
        1,
        seriesTpl,
      )

      // 配置y轴
      if (unitShow && unitShow.length > 0) {
        let yAxis0: YAXisComponentOption | undefined = undefined
        let yAxis1: YAXisComponentOption | undefined = undefined
        if (unitShow[0]) {
          yAxis0 = defaultsDeep({ name: mainData?.unit }, yAxisConfig)
        }
        if (unitShow[1]) {
          yAxis1 = defaultsDeep({ name: compareData?.unit }, yAxisConfig)
        }
        if (yAxis0 && yAxis1) {
          configResult.yAxis = [yAxis0, yAxis1]
          configResult.series[1].yAxisIndex = 1
        } else if (yAxis0) {
          configResult.yAxis = yAxis0
        } else if (yAxis1) {
          configResult.yAxis = yAxis1
        }
      } else {
        configResult.yAxis.name = mainData?.unit
      }

      configResult.graphic[0].invisible = true
    } else {
      configResult.graphic[0].invisible = false
      configResult.series = []
    }
  }

  return configResult
}

const stopLine = () => {
  const zr = chartComponent.value?.getZr()
  if (zr) {
    const lineGraphics = zr.storage.getDisplayList()
    for (const el of lineGraphics) {
      if (el.type === "ec-polyline") {
        if (typeof el.id === "string") {
          zr.remove(el)
        }
      }
    }
    // zr.flush()
  }
}

const updateFn = () => {
  const zr = chartComponent.value?.getZr()
  if (zr) {
    const lineGraphics = zr.storage.getDisplayList()

    for (const el of lineGraphics) {
      if (el.type === "ec-polyline") {
        if (typeof el.id === "string") {
          continue
        }
        const subId = `animated-${el.id}`
        const subEl = lineGraphics.find(item => item.id.toString() === subId)
        if (subEl) {
          // const nel = cloneDeep(el)
          // delete nel.parent
          // subEl.stopAnimation()
          // subEl.path.data = nel.path.data
          // subEl.shape = nel.shape
          // subEl.dirty()
          // subEl.show()
          // 重新启动动画
          // let duration = props.lineAnimateDuration // 动画总时长
          // let delay = 0
          // let animations = [] as {
          //   when: number
          //   style: { stroke: { type: string; x: number; y: number; x2: number; y2: number; colorStops: ColorStop[] } }
          // }[]
          // for (let i = 0; i <= 20; i++) {
          //   delay = i * (duration / 20)
          //   const colorStops = [] as ColorStop[]
          //   if (i === 0) {
          //     colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
          //     colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
          //   } else if (i === 20) {
          //     colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
          //     colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
          //   } else {
          //     colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
          //     colorStops.push({ offset: (1 / 20) * i - 0.05, color: "rgba(255,255,255,0)" })
          //     colorStops.push({ offset: (1 / 20) * i, color: "rgba(255,255,255,0.5)" })
          //     colorStops.push({ offset: Math.min((1 / 20) * i + 0.08, 1), color: "rgba(255,255,255,1)" })
          //     colorStops.push({ offset: Math.min((1 / 20) * i + 0.09, 1), color: "rgba(255,255,255,0)" })
          //     colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
          //   }
          //   animations.push({
          //     when: delay,
          //     style: {
          //       stroke: {
          //         type: "linear", // 线性渐变
          //         x: 0,
          //         y: 0,
          //         x2: 1,
          //         y2: 0,
          //         colorStops,
          //       },
          //     },
          //   })
          // }
          // let animation = subEl.animate("style", true)
          // animations.forEach(anim => {
          //   animation = animation.when(anim.when, anim.style)
          // })
          // animation.start()
        } else {
          const nel = cloneDeep(el)
          delete nel.parent
          nel.id = subId
          nel.style.stroke = {
            type: "linear", // 线性渐变
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: "rgba(255,255,255,1)" }, // 结束颜色
              { offset: 1, color: "rgba(255,255,255,1)" }, // 结束颜色
            ],
          }
          // 添加到 zr
          zr.add(nel)

          let duration = props.lineAnimateDuration // 动画总时长
          let delay = 0
          let animations = [] as {
            when: number
            style: { stroke: { type: string; x: number; y: number; x2: number; y2: number; colorStops: ColorStop[] } }
          }[]

          for (let i = 0; i <= 20; i++) {
            delay = i * (duration / 20)
            const colorStops = [] as ColorStop[]
            if (i === 0) {
              colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
              colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
            } else if (i === 20) {
              colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
              colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
            } else {
              colorStops.push({ offset: 0, color: "rgba(255,255,255,0)" })
              colorStops.push({ offset: (1 / 20) * i - 0.05, color: "rgba(255,255,255,0)" })
              colorStops.push({ offset: (1 / 20) * i, color: "rgba(255,255,255,0.5)" })
              colorStops.push({ offset: Math.min((1 / 20) * i + 0.08, 1), color: "rgba(255,255,255,1)" })
              colorStops.push({ offset: Math.min((1 / 20) * i + 0.09, 1), color: "rgba(255,255,255,0)" })
              colorStops.push({ offset: 1, color: "rgba(255,255,255,0)" })
            }
            animations.push({
              when: delay,
              style: {
                stroke: {
                  type: "linear", // 线性渐变
                  x: 0,
                  y: 0,
                  x2: 1,
                  y2: 0,
                  colorStops,
                },
              },
            })
          }

          // // 应用动画
          let animation = nel.animate("style", true)
          animations.forEach(anim => {
            animation = animation.when(anim.when, anim.style)
          })

          animation.start()
        }
      }
    }

    zr.flush()
  }
}

const loading = () => {
  componentLoading.value = true
}
const setData = (
  data:
    | [DataItem, DataItem]
    | {
        dataList: [DataItem, DataItem]
        config?: EChartsOption
        unitShow?: [boolean, boolean]
      },
  option?: EChartsOption,
  unitShow?: [boolean, boolean],
) => {
  componentLoading.value = false
  if (props.lineAnimate) {
    stopLine()
  }
  if (Array.isArray(data)) {
    const result = componentPropEngine({ dataList: data, config: option, unitShow })
    chartConfig.value = result
  } else {
    const result = componentPropEngine(data)
    chartConfig.value = result
  }
  if (props.lineAnimate) {
    setTimeout(() => {
      updateFn()
    }, 1000)
  }
}
defineExpose({ setData, loading, show, hide, changeVisible })
</script>

<style lang="scss">
.compare-line-chart {
  height: 100%;
}
</style>
